import { decamelizeKeys } from "humps";
import api, { ApiError } from "@/services/api";
// @ts-expect-error - api is not typed
const { get, post } = api.getPrefixedMethods("intake");
export const SUBMIT_STILL_RUNNING = "The submit is still running. Check this order in a few minutes.";
const submitPath = (orderId) => `orders/${orderId}/submit-clinical-review/`;
const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const retryable = (error) => {
    const status = error?.response?.status;
    return status === undefined || status >= 500;
};
export class SubmitAbandoned extends Error {
}
export const submitFailure = (poll) => new ApiError({ message: "", response: { status: 400, data: decamelizeKeys(poll.error) } });
export const clinicalReviewSubmitService = {
    /** One read of this order's submit status, or of one submit when submitId is given. */
    status: (orderId, submitId) => get(submitPath(orderId), submitId ? { params: { submit: submitId } } : undefined),
    /**
     * First status check when a page opens. Retries network and 5xx errors like wait();
     * null when refused, when isActive() turns false or after waitMs.
     */
    async pending(orderId, options = {}) {
        const { isActive = () => true, pollMs = 1500, waitMs = 180_000 } = options;
        const deadline = Date.now() + waitMs;
        while (Date.now() < deadline && isActive()) {
            try {
                return await clinicalReviewSubmitService.status(orderId);
            }
            catch (error) {
                if (!retryable(error))
                    return null;
                await pause(pollMs);
            }
        }
        return null;
    },
    /**
     * Poll this order's submit until it ends: the PA when done, null when idle, the submit's error when failed.
     * Retries network and 5xx errors; throws SubmitAbandoned when isActive() turns false
     * and SUBMIT_STILL_RUNNING after waitMs.
     */
    async wait(orderId, options = {}) {
        const { isActive = () => true, pollMs = 1500, waitMs = 180_000, submitId } = options;
        const deadline = Date.now() + waitMs;
        while (Date.now() < deadline) {
            if (!isActive())
                throw new SubmitAbandoned();
            let poll;
            try {
                poll = await clinicalReviewSubmitService.status(orderId, submitId);
            }
            catch (error) {
                if (!retryable(error))
                    throw error;
                await pause(pollMs);
                continue;
            }
            if (poll.status === "done")
                return poll.orderInfo;
            if (poll.status === "failed")
                throw submitFailure(poll);
            if (poll.status === "idle")
                return null;
            await pause(pollMs);
        }
        throw new Error(SUBMIT_STILL_RUNNING);
    },
    /** Start this order's submit and wait() for it; a reply with no submitId is returned as is. */
    async submit(orderId, payload = {}, options = {}) {
        const answer = await post(submitPath(orderId), payload);
        if (!answer?.submitId)
            return answer;
        return clinicalReviewSubmitService.wait(orderId, { ...options, submitId: answer.submitId });
    },
};
